import React, { useRef, useState } from 'react';
import { motion, useInView } from 'framer-motion';
import { Star, Quote, ChevronLeft, ChevronRight } from 'lucide-react';

const testimonials = [
  {
    id: 1,
    name: 'Rajesh Patel',
    role: 'Owner, Patel Electronics',
    location: 'Ahmedabad',
    image: 'https://images.unsplash.com/photo-1562228802-4b1052d0f845?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwyfHxwcm9mZXNzaW9uYWwlMjBoZWFkc2hvdCUyMHBvcnRyYWl0fGVufDB8fHx8MTc3MzkwNjY2NXww&ixlib=rb-4.1.0&q=85',
    content: 'Excellent work by Ascent International! They delivered a stunning LED sign board for my electronics showroom. The quality is top-notch and it really makes our shop stand out.',
    rating: 5,
  },
  {
    id: 2,
    name: 'Meena Shah',
    role: 'Marketing Head, Shivalik Group',
    location: 'Ahmedabad',
    image: 'https://images.pexels.com/photos/30004324/pexels-photo-30004324.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
    content: 'We have worked with Ascent for multiple real estate projects. Their signage solutions are premium quality and they always deliver on time. Professional team!',
    rating: 5,
  },
  {
    id: 3,
    name: 'Amit Desai',
    role: 'Store Manager, Nike Ahmedabad',
    location: 'SG Highway',
    image: 'https://images.unsplash.com/photo-1672685667592-0392f458f46f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwzfHxwcm9mZXNzaW9uYWwlMjBoZWFkc2hvdCUyMHBvcnRyYWl0fGVufDB8fHx8MTc3MzkwNjY2NXww&ixlib=rb-4.1.0&q=85',
    content: 'The acrylic signage they created for our store is exactly what we needed - modern, sleek, and perfectly aligned with Nike brand guidelines.',
    rating: 5,
  },
  {
    id: 4,
    name: 'Priya Sharma',
    role: 'Owner, Bagline Store',
    location: 'Satellite, Ahmedabad',
    image: 'https://images.unsplash.com/photo-1562228802-4b1052d0f845?crop=entropy&cs=srgb&fm=jpg',
    content: 'Best signage company in Gujarat! They handled our complete store branding from facade to interior signage. The 3D letters look amazing!',
    rating: 5,
  },
  {
    id: 5,
    name: 'Vikram Mehta',
    role: 'Project Manager, Venus Infra',
    location: 'Bopal, Ahmedabad',
    image: 'https://images.unsplash.com/photo-1672685667592-0392f458f46f?crop=entropy&cs=srgb&fm=jpg',
    content: 'We chose Ascent for our township project signage requirements. Their ability to handle large-scale projects with precision is commendable.',
    rating: 5,
  },
  {
    id: 6,
    name: 'Kavita Joshi',
    role: 'Owner, Fashion Boutique',
    location: 'CG Road, Ahmedabad',
    image: 'https://images.pexels.com/photos/30004324/pexels-photo-30004324.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
    content: 'The neon signage they created for my boutique is absolutely gorgeous! It adds such a premium feel to my store. Thank you Ascent team!',
    rating: 5,
  },
];

const TestimonialCard = ({ testimonial }) => {
  return (
    <div
      className="testimonial-card h-full flex flex-col"
      data-testid={`testimonial-card-${testimonial.id}`}
    >
      {/* Quote Icon */}
      <div className="mb-4">
        <Quote className="w-8 h-8 text-sky-200" />
      </div>

      {/* Content */}
      <p className="text-slate-600 font-['Manrope'] text-sm md:text-base leading-relaxed flex-grow mb-6">
        "{testimonial.content}"
      </p>

      {/* Rating */}
      <div className="flex gap-1 mb-4">
        {[...Array(testimonial.rating)].map((_, i) => (
          <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />
        ))}
      </div>

      {/* Author */}
      <div className="flex items-center gap-4">
        <img
          src={testimonial.image}
          alt={testimonial.name}
          className="w-12 h-12 rounded-full object-cover border-2 border-sky-100"
        />
        <div>
          <h4 className="text-slate-800 font-['Oswald'] font-bold">
            {testimonial.name}
          </h4>
          <p className="text-xs text-slate-500 font-['Manrope']">
            {testimonial.role} • {testimonial.location}
          </p>
        </div>
      </div>
    </div>
  );
};

const TestimonialsSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });
  const [currentIndex, setCurrentIndex] = useState(0);
  const itemsPerPage = 3;

  const nextSlide = () => {
    setCurrentIndex((prev) => 
      prev + itemsPerPage >= testimonials.length ? 0 : prev + itemsPerPage
    );
  };

  const prevSlide = () => {
    setCurrentIndex((prev) => 
      prev - itemsPerPage < 0 ? Math.max(0, testimonials.length - itemsPerPage) : prev - itemsPerPage
    );
  };

  const visibleTestimonials = testimonials.slice(currentIndex, currentIndex + itemsPerPage);

  return (
    <section
      id="testimonials"
      className="py-24 md:py-32 bg-white relative overflow-hidden"
      data-testid="testimonials-section"
    >
      {/* Background */}
      <div className="absolute inset-0 opacity-50">
        <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-sky-50 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 right-1/4 w-64 h-64 bg-violet-50 rounded-full blur-3xl"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 relative z-10">
        {/* Section Header */}
        <motion.div
          ref={ref}
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.6 }}
        >
          <span className="text-sky-500 text-sm font-semibold uppercase tracking-[0.2em] mb-4 block font-['Manrope']">
            Testimonials
          </span>
          <h2 className="section-heading mb-6">
            WHAT OUR <span className="gradient-text">CLIENTS SAY</span>
          </h2>
          <p className="section-subheading mx-auto">
            Don't just take our word for it - hear from our satisfied clients across Ahmedabad and Gujarat.
          </p>
        </motion.div>

        {/* Testimonials Grid */}
        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          {visibleTestimonials.map((testimonial, index) => (
            <motion.div
              key={testimonial.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: index * 0.1 }}
            >
              <TestimonialCard testimonial={testimonial} />
            </motion.div>
          ))}
        </motion.div>

        {/* Navigation */}
        <div className="flex items-center justify-center gap-4">
          <button
            onClick={prevSlide}
            className="w-12 h-12 rounded-full border border-gray-200 flex items-center justify-center hover:bg-slate-50 hover:border-sky-300 transition-all duration-300"
            data-testid="testimonials-prev"
            aria-label="Previous testimonials"
          >
            <ChevronLeft className="w-5 h-5 text-slate-600" />
          </button>
          <div className="flex gap-2">
            {Array.from({ length: Math.ceil(testimonials.length / itemsPerPage) }).map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrentIndex(i * itemsPerPage)}
                className={`w-2 h-2 rounded-full transition-all duration-300 ${
                  Math.floor(currentIndex / itemsPerPage) === i
                    ? 'bg-sky-500 w-6'
                    : 'bg-slate-200 hover:bg-slate-300'
                }`}
                aria-label={`Go to testimonial group ${i + 1}`}
              />
            ))}
          </div>
          <button
            onClick={nextSlide}
            className="w-12 h-12 rounded-full border border-gray-200 flex items-center justify-center hover:bg-slate-50 hover:border-sky-300 transition-all duration-300"
            data-testid="testimonials-next"
            aria-label="Next testimonials"
          >
            <ChevronRight className="w-5 h-5 text-slate-600" />
          </button>
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
